import { useContext, useState, useEffect } from "react";
import {
  Menu,
  ActionIcon,
  Group,
  Stack,
  Text,
  Divider,
  Title,
  ThemeIcon,
  Tooltip,
  Progress,
  Indicator,
  Table,
} from "@mantine/core";
import { IconDownload, IconX } from "@tabler/icons-react";
import { useViewportSize } from "@mantine/hooks";
import axios from "axios";
import { ExportsContext } from "../contexts/ExportsContext";
import {
  ExportFormats,
  getExportStatus,
  generateExportName,
  timeAgo,
} from "../utils/constants";
import ExportService from "../services/ExportService";
import classes from "../styles/ExportsMenu.module.css";
import { showSuccessNotification } from "../utils/notifications";

const ExportsMenu = () => {
  const {
    exportsList,
    updateExport,
    activeExportsCount,
    fetchExports,
    highlightExportId,
    setHighlightExportId,
    menuOpened,
    setMenuOpened,
  } = useContext(ExportsContext);
  const [progressMap, setProgressMap] = useState({});
  const { width: vw, height: vh } = useViewportSize();

  useEffect(() => {
    if (highlightExportId && menuOpened) {
      // Wait for the menu to render, then scroll to and highlight the row
      const timer = setTimeout(() => {
        const row = document.querySelector(
          `[data-testid="export-row-${highlightExportId}"]`
        );
        if (row) {
          row.scrollIntoView({ behavior: "smooth", block: "nearest" });
        }
      }, 100);

      // Clear the highlight after animation completes
      const clearTimer = setTimeout(() => {
        setHighlightExportId(null);
      }, 3500);

      return () => {
        clearTimeout(timer);
        clearTimeout(clearTimer);
      };
    }
  }, [highlightExportId, menuOpened, setHighlightExportId]);
  const menuWidth = Math.min(600, vw - 10);
  const maxHeight = Math.min(vh - 200, 500);

  const handleDownload = async (exportObj) => {
    try {
      setProgressMap((prev) => ({ ...prev, [exportObj.id]: 0 }));

      const response = await ExportService.getExportOutputs(exportObj.id);
      const { sasUrl, extension } = response.results[0];

      axios
        .get(sasUrl, {
          responseType: "blob",
          onDownloadProgress: (progressEvent) => {
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
            setProgressMap((prev) => ({
              ...prev,
              [exportObj.id]: percentCompleted,
            }));
          },
        })
        .then(async (res) => {
          const url = window.URL.createObjectURL(new Blob([res.data]));
          const ext = extension.toLowerCase();
          const link = document.createElement("a");
          link.href = url;
          link.download =
            exportObj?.fileDisplayNames?.[0] ||
            generateExportName(exportObj) + (ext ? `.${ext}` : "");
          link.style.display = "none";
          document.body.appendChild(link);
          link.click();
          link.remove();
          window.URL.revokeObjectURL(url);

          await updateExport(exportObj.id, { status: "DL" });
          setProgressMap((prev) => ({ ...prev, [exportObj.id]: null }));
        })
        .catch((error) => {
          console.error("Error downloading the file:", error);
          setProgressMap((prev) => ({ ...prev, [exportObj.id]: null }));
        });
    } catch (error) {
      console.error("Error downloading the file:", error);
      setProgressMap((prev) => ({ ...prev, [exportObj.id]: null }));
    }
  };

  const handleClear = async (exportObj) => {
    try {
      await updateExport(exportObj.id, { isHidden: true });
      // Refetch exports to update the list without the hidden export
      await fetchExports();
      showSuccessNotification(
        "Success",
        `Export "${generateExportName(exportObj)}" removed successfully.`
      );
    } catch (error) {
      console.error("Error hiding the export:", error);
    }
  };

  return (
    <Menu
      withArrow
      width={menuWidth}
      opened={menuOpened}
      onChange={setMenuOpened}
    >
      <Menu.Target>
        <Indicator
          label={activeExportsCount}
          disabled={activeExportsCount === 0}
          processing
          position="top-end"
          size={17}
          color="teal"
        >
          <ActionIcon
            variant="subtle"
            color="gray"
            c="var(--mantine-color-text)"
            size="md"
            data-testid="exports-menu-button"
          >
            <IconDownload size={16} />
          </ActionIcon>
        </Indicator>
      </Menu.Target>
      <Menu.Dropdown>
        <Stack gap="xs" p="xs">
          <Title order={5} py={3}>
            Exports
          </Title>
          <Divider />
          {exportsList && exportsList.length !== 0 ? (
            <Table.ScrollContainer minWidth="100%" maxHeight={maxHeight}>
              <Table
                withColumnBorders={false}
                withRowBorders={false}
                striped={false}
                highlightOnHover={false}
                verticalSpacing={6}
                style={{ tableLayout: "fixed", width: "100%" }}
              >
                <Table.Tbody>
                  {exportsList.map((exportObj) => {
                    const formatObj = ExportFormats.find(
                      (format) => format.name === exportObj.format
                    );
                    const statusObj = getExportStatus(exportObj.status);
                    const FormatIcon = formatObj?.Icon;
                    const StatusIcon = statusObj?.Icon;
                    const progress = progressMap[exportObj.id];
                    const isActive =
                      exportObj.status === "PE" || exportObj.status === "PR";

                    return (
                      <Table.Tr
                        key={exportObj.id}
                        data-testid={`export-row-${exportObj.id}`}
                        className={
                          highlightExportId === exportObj.id
                            ? classes.highlight
                            : undefined
                        }
                      >
                        <Table.Td style={{ width: "50px" }}>
                          {FormatIcon && (
                            <ThemeIcon
                              variant="light"
                              color="teal"
                              size={35}
                              radius={4}
                            >
                              <FormatIcon {...(formatObj.iconProps || {})} />
                            </ThemeIcon>
                          )}
                        </Table.Td>
                        <Table.Td style={{ width: "auto" }}>
                          <Stack gap={2}>
                            <Text
                              size="sm"
                              fw={500}
                              truncate="end"
                              className={isActive ? classes.pulse : ""}
                            >
                              {generateExportName(exportObj)}
                            </Text>
                            {progress !== null && progress !== undefined && (
                              <Progress
                                value={progress}
                                size="xs"
                                style={{ marginTop: 4, marginBottom: 4 }}
                              />
                            )}
                            <Group gap={3} align="center">
                              {StatusIcon && (
                                <ThemeIcon
                                  variant="subtle"
                                  size={14}
                                  style={{ transform: "translateY(-1px)" }}
                                  color={statusObj.color || "default"}
                                >
                                  <StatusIcon
                                    {...(statusObj.iconProps || {})}
                                  />
                                </ThemeIcon>
                              )}
                              <Text size="xs" c="dimmed">
                                {statusObj ? statusObj.label : exportObj.status}{" "}
                                • {timeAgo(exportObj.createdAt)}
                              </Text>
                            </Group>
                          </Stack>
                        </Table.Td>
                        <Table.Td style={{ width: "80px" }}>
                          <Group gap="xs" wrap="nowrap">
                            <Tooltip label="Download Export">
                              <ActionIcon
                                variant="subtle"
                                color="default"
                                onClick={() => handleDownload(exportObj)}
                                disabled={
                                  !(
                                    exportObj.status === "DL" ||
                                    exportObj.status === "CO"
                                  ) ||
                                  (progress !== null && progress !== undefined)
                                }
                              >
                                <IconDownload size={16} />
                              </ActionIcon>
                            </Tooltip>
                            <Tooltip label="Delete Export">
                              <ActionIcon
                                variant="subtle"
                                color="default"
                                onClick={() => handleClear(exportObj)}
                                data-testid={`export-delete-button-${exportObj.id}`}
                              >
                                <IconX size={16} />
                              </ActionIcon>
                            </Tooltip>
                          </Group>
                        </Table.Td>
                      </Table.Tr>
                    );
                  })}
                </Table.Tbody>
              </Table>
            </Table.ScrollContainer>
          ) : (
            <Stack gap={2} align="center" justify="center" w="100%" h={380}>
              <ThemeIcon size={120} radius={80} color="teal" variant="light">
                <IconDownload size={45} />
              </ThemeIcon>
              <Text size="sm" fw={500} pt="xs">
                No exports available
              </Text>
              <Text size="xs" c="dimmed" mb="lg" px={50} align="center">
                Create exports from the Inspections page to see them here
              </Text>
            </Stack>
          )}
        </Stack>
      </Menu.Dropdown>
    </Menu>
  );
};

export default ExportsMenu;
